import { useState, useEffect } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui';

/**
 * Post-auth holding page for a user whose access is being withheld.
 *
 * Since YAR-412 (open beta) this is NOT part of the signup flow: a new
 * account is approved as it is provisioned and goes straight from sign-in
 * into onboarding. The page is reached only when access is deliberately
 * withheld — `app_config.signups_open = 'false'` pausing registrations
 * globally, or an admin un-approving a single user — and it promotes the
 * user the moment that is reversed, without a reload.
 *
 * Not to be confused with `WAITLIST_URL` (config/urls.ts), the public
 * early-access form on the marketing site that signed-out visitors get.
 */
export function Waitlist() {
  const navigate = useNavigate();
  const { signOut, user, loading } = useAuth();
  const [checkingApproval, setCheckingApproval] = useState(true);

  // Watch `users.approved` for this user and navigate as soon as it flips.
  //
  // Initial fetch handles the race where approval already happened between
  // sign-in and reaching this page (e.g. admin clicked Pre-approve while the
  // user was loading); the Realtime subscription handles the live case where
  // an admin approves them while this page is open. The previous 30s
  // setInterval polled the table — adequate but laggy and noisy on the DB;
  // realtime gives instant promotion at zero ongoing cost.
  useEffect(() => {
    if (!user || loading) {
      setCheckingApproval(false);
      return;
    }

    let mounted = true;

    const promoteIfApproved = (approved: boolean | undefined | null) => {
      if (!mounted) return;
      if (approved) {
        navigate('/', { replace: true });
      } else {
        setCheckingApproval(false);
      }
    };

    // One immediate check. If true, we navigate and the subscribe below
    // won't have anything to do; if false, we wait for the realtime push.
    supabase
      .from('users')
      .select('approved')
      .eq('supabase_id', user.id)
      .maybeSingle()
      .then(({ data }) => promoteIfApproved(data?.approved));

    const channel = supabase
      .channel(`waitlist-approval-${user.id}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'users',
          filter: `supabase_id=eq.${user.id}`,
        },
        (payload) => {
          const next = payload.new as { approved?: boolean } | null;
          promoteIfApproved(next?.approved);
        },
      )
      .subscribe();

    return () => {
      mounted = false;
      void supabase.removeChannel(channel);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally keyed on user.id, not user object
  }, [user?.id, loading, navigate]);

  // Redirect unauthenticated users to login
  if (!loading && !user) {
    return (
      <Navigate
        to="/"
        replace
      />
    );
  }

  if (loading || checkingApproval) {
    return (
      <div className="min-h-dvh flex items-center justify-center p-8 bg-muted">
        <div className="bg-background rounded-md p-10 w-full max-w-md shadow-md text-center">
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  const handleSignOut = async () => {
    try {
      await signOut();
    } catch {
      // ignore
    }
  };

  return (
    <div className="min-h-dvh flex items-center justify-center p-8 bg-muted">
      <div className="bg-background rounded-md p-10 w-full max-w-md shadow-md text-center">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-primary">YARUTA</h1>
          <p className="text-xs text-muted-foreground mt-1">
            School communication, organized.
          </p>
        </div>

        <div className="mb-2 flex justify-center">
          <div className="w-16 h-16 rounded-full bg-primary-light flex items-center justify-center">
            <svg
              width="32"
              height="32"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="text-primary"
            >
              <circle
                cx="12"
                cy="12"
                r="10"
              />
              <polyline points="12 6 12 12 16 14" />
            </svg>
          </div>
        </div>

        <h2 className="text-xl font-semibold mb-2">You're on the list!</h2>
        <p className="text-sm text-muted-foreground mb-6">
          Thanks for signing up. We're onboarding users gradually during our
          beta. You'll receive an email at{' '}
          <strong className="text-foreground">{user?.email}</strong> when your
          account is ready.
        </p>

        <div className="bg-primary-light rounded-md p-4 mb-6 text-left">
          <p className="text-sm font-medium text-primary mb-2">
            Want to skip the wait?
          </p>
          <p className="text-sm text-muted-foreground">
            Ask someone who already uses YARUTA to invite you to their
            household. Invited users get immediate access.
          </p>
        </div>

        <Button
          onClick={handleSignOut}
          variant="outline"
          fullWidth
        >
          Sign out
        </Button>
      </div>
    </div>
  );
}
